import { Link, useParams } from 'react-router-dom';
import './auth.scss';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { yupPasswordValidator } from '@myinnsworth/helpers/passwordHelpers';
import { authenticationHttpService } from '@myinnsworth/httpServices';
import axios, { AxiosError } from 'axios';

const resetPasswordSchema = yup.object({
    password: yupPasswordValidator,
    confirmPassword: yup.string()
        .required('You must confirm your password')
        .oneOf([yup.ref('password')], 'Passwords do not match')
  }).required();

interface IResetPasswordSchema {
    password: string;
    confirmPassword: string;
}

enum PasswordResetState {
    None,
    InvalidToken,
    ValidatingToken,
    Success
}

export default function ResetPassword() {
    const params = useParams();
    const [resetState, setResetState] = useState<PasswordResetState>(params.token ? PasswordResetState.ValidatingToken : PasswordResetState.InvalidToken);
    const {register: registerInput, handleSubmit, clearErrors, setError, formState: {errors} } = useForm<IResetPasswordSchema>({
        resolver: yupResolver(resetPasswordSchema)
    });

    const onSubmit = async (formData: IResetPasswordSchema) => {
        clearErrors();
        try {
            await authenticationHttpService.resetPassword({
                ...formData,
                token: params.token!
            });
            setResetState(PasswordResetState.Success);
        } catch(ex) {
            if(axios.isAxiosError(ex)) {
                const error = ex as AxiosError;
                switch(error.response!.status) {
                    case 400: { //400 - Token expired
                        setResetState(PasswordResetState.InvalidToken);
                        break;
                    }
                    case 406: { //406 - Passwords do not match
                        setError('confirmPassword', { message: 'Passwords do not match'});
                        break;
                    }
                    case 417: { //417 - Password complexity
                        setError('password', { message: 'Password does not meet complexity rules'});
                        break;
                    }
                }
            }
        }
    };

    const validateToken = useCallback(async () => {
        if(params.token) {
            const isValid = await authenticationHttpService.validateResetPasswordToken(params.token!);
            setResetState(isValid ? PasswordResetState.None : PasswordResetState.InvalidToken);
        }
    }, [params.token]);

    //First we need to validate the token
    useEffect(() => {
        validateToken();
    }, [validateToken]);

    return (<>
        {resetState === PasswordResetState.None && (
            <>
            <h6>Log in to My.Innsworth</h6>
            <div className="spacing-l"></div>

            <form className="auth-form-container" noValidate onSubmit={handleSubmit(onSubmit)}>
            <div className={clsx('form-control', 'normal', { 'error': errors.password })}>
                    <label >Password</label>
                    <input {...registerInput('password')}
                        type="password"
                        className={clsx('icon-person', { 'error': errors.password })}
                        placeholder='Password' />
                    {errors.password && <p>{errors.password.message}</p>}
                    {!errors.password && <p>Please enter a new password</p>}
                </div>
                
                <div className="spacing-m"></div>

                <div className={clsx('form-control', 'normal', { 'error': errors.confirmPassword })}>
                    <label >Confirm Password</label>
                    <input {...registerInput('confirmPassword')}
                        type="password"
                        className={clsx('icon-person', { 'error': errors.confirmPassword })}
                        placeholder='Password' />
                    {errors.confirmPassword && <p>{errors.confirmPassword.message}</p>}
                    {!errors.confirmPassword && <p>Please confirm your new password</p>}
                </div>

                <div className="spacing-m"></div>

                <div className="flex justify-center">
                    <button type="submit" className="btn primary medium">Reset</button>
                </div>

                <div className="spacing-m"></div>

                <div className="flex direction-column align-center">
                    <Link className='link secondary' to={"/auth/sign-in"} >Back to login</Link>
                </div>

            </form>
        </>
        )}

        {resetState === PasswordResetState.Success && (<>
            <h6 className='text-center'>Your password has been successfully reset</h6>
            <div className="spacing-l"></div>

            <div className="flex direction-column align-center">
                <Link className='btn primary large back' to={"/auth/sign-in"} >Back</Link>
            </div>
        </>)}

        {resetState === PasswordResetState.InvalidToken && (<>
            <h6 className='text-center'>Password reset has expired, please try again</h6>
            <div className="spacing-l"></div>

            <div className="flex direction-column align-center">
                <Link className='btn primary large back' to={"/auth/forgot-password"} >Forgot Password</Link>
            </div>
        </>)}

        {resetState === PasswordResetState.ValidatingToken && (<>
            <h6 className='text-center'>Validating....</h6>
        </>)}
    </>)
}